/* eslint-disable camelcase */
import React, {useEffect, useMemo, useState} from 'react';
import {SidebarLink} from '../Sidebar';
import {default as Icon} from '../Icon';
import {ic_vpn_key} from 'react-icons-kit/md/ic_vpn_key';
import {MediaAccessRightsModal} from './MediaAccessRightsModal';
import {BrandType, fetchBrandList} from '@sinclair-digital/stl-common/src/models/Brand';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import classNames from 'classnames';
import styles from './MediaAccessRights.scss';

interface propTypes {
  selectedBrandUuids: string[];         // List of brand uuids that have access permission for this media. An empty array means ALL brands are selected.
  availableBrandUuids: string[];        // List of brand uuids that the user has permission for
  availableAdminBrandUuids: string[];   // List of brand uuids that the user is an admin for
  onSave: (brandUuidList: string[]) => void;
  isEditable: boolean;
}

export const MediaAccessRightsSidebarItem = (props: propTypes) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [allBrands, setAllBrands] = useState<BrandType[]>([]);

  useEffect(() => {
    fetchBrandList()
      .then(brands => setAllBrands(brands.data))
      .catch(() => messageHandler('error', 'Failed to fetch brand list'));
  }, []);

  /**
   * checks if selectedBrandUuid is an empty array
   * if empty then transform to an array with ALL brand uuids
   */
  const _selectedBrandUuids = useMemo(() => {
    if (props.selectedBrandUuids.length > 0) {
      return props.selectedBrandUuids;
    }
    return allBrands.map(({uuid}) => uuid);
  }, [props.selectedBrandUuids, allBrands]);

  const toggleModal = () => setIsModalOpen(prevState => !prevState);

  const onClickHandler = () => props.isEditable  && toggleModal();

  /**
   *  returns text of brand name with access to the media
   *  3 brands name max, with commas in between, rest will be displayed as "+ x more"
   *  ex. "KOMO, DEMO, SIND, +10 more"
   *  @return {string}
   */
  const assignedBrandListText = (): string => {
    if (!_selectedBrandUuids || allBrands.length === 0) return '';
    let text = '';

    _selectedBrandUuids.forEach((brandUuid, index) => {
      if (index < 3) {
        const brand = allBrands.find(({uuid}) => uuid === brandUuid);
        text += `${index !== 0 ? ', ' : ''}${brand?.callLetters}`;
      }
    });

    if (_selectedBrandUuids.length > 3) {
      text += ` + ${_selectedBrandUuids.length - 3} more`;
    }

    return `Assigned to ${text}`;
  };


  const modalProps = {
    ...props,
    selectedBrandUuids: _selectedBrandUuids,
    isModalOpen,
    allBrands,
    toggleModal,
  };

  return (
    <>
      <SidebarLink
        icon={<Icon icon={ic_vpn_key} size={22}/>}
        // @ts-ignore
        onClick={onClickHandler}
        className={classNames(styles.mediaAccessRightsSidebarItem, {[styles.isDisabled]: !props.isEditable})}
        title={props.isEditable ? '' : 'User doesn\'t have rights to this file, please contact your admin.'}
      >
        <div>Manage Permissions</div>
        <div className={styles.assignedBrandText}>{assignedBrandListText()}</div>
      </SidebarLink>
      {allBrands.length > 0 &&
        <MediaAccessRightsModal {...modalProps}/>
      }
    </>
  );
};
